/**
 * HouseholdProductSettings — the per-HouseholdProduct soft-preferences surface
 * (T12.1.7 / restyled T12.1.7.1 / reordered T27.2.7.10). Composed into the
 * product detail screen. Built to the planning-shopping prototype `#s-settings`,
 * top-to-bottom in the prototype order:
 *
 *   1. **Basics** — an always-open compact group: How many to add (the
 *      `default_quantity` stepper, injected by the detail page as `basicsLeading`)
 *      · Size (the T36 measurement picker) · How often (the cadence stepper).
 *   2. **Product preferences** — the foldable SystemProduct picker, injected by
 *      the detail page via `productPreferencesSlot` so it sits between Basics and
 *      Brand preferences in the prototype order.
 *   3. **Brand preferences** — a foldable `<details>` (collapsed) with a summary
 *      value line (★ {preferred} · {n} subs); internals unchanged.
 *   4. **Store preferences** (where to buy) — a foldable `<details>` (collapsed)
 *      with a summary value line ({retailers} · {n} store); internals unchanged.
 *
 * Each block is one flat bordered `.setgroup`. Brand/retailer/store preferences
 * render as `.tag` / `.tag.sub` / `.tag.excl` chips with a trailing ✕; the
 * "+ Add" chip opens an inline search (InlineAddSearch). The cadence stepper
 * auto-saves on each −/+; a "Let us learn it" link clears to NULL.
 *
 * Every preference is SOFT and advisory — the surface states so and never
 * hard-filters anything; the ranking that weighs these lives in T12.5. Each
 * mutation invalidates the preferences query so the lists reflect the new state
 * and persist across reload.
 *
 * All styles flow from tokens (CP5). No hex, no raw px literals.
 */

import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import {
  useHouseholdProductPreferences,
  useHouseholdProduct,
  useBrands,
  useStores,
  useSearchSystemProducts,
  useLinkBrandPreference,
  useSetBrandPreference,
  useUnlinkBrandPreference,
  useLinkRetailerPreference,
  useUnlinkRetailerPreference,
  useLinkStoreLocationPreference,
  useUnlinkStoreLocationPreference,
  useSetHouseholdProductCadence,
  useMeasurementChoices,
  useSetMeasurement,
  useSetDisplayUnit,
  useUseSuggestedMeasurement,
} from '@shopista/shared/api/hooks';
import type { SystemProductSearchParams } from '@shopista/shared/types';
import { apiErrorMessage } from '@shopista/shared/api';
import {
  formatResolvedMeasurement,
  availableDisplayUnits,
  type MeasurementChoice,
} from '@shopista/shared/types';
import { Spinner, Modal } from '../../components';
import { InlineAddSearch, type AddOption } from './InlineAddSearch';
import {
  setgroupStyle,
  h4Style,
  hintStyle,
  subheadStyle,
  setlabelStyle,
  tagsRowStyle,
  tagPrefStyle,
  tagSubStyle,
  tagExclStyle,
  tagXStyle,
  pickListStyle,
  pickListSubheadStyle,
  pickRowStyle,
  pickRowOnStyle,
  pickMarkStyle,
  cadrowStyle,
  cadWordStyle,
  stepperStyle,
  stepperBtnStyle,
  stepperValueStyle,
  learnLinkStyle,
  sizeUnitRowStyle,
  sizeUnitLabelStyle,
  unitSegStyle,
  unitSegBtnStyle,
  unitSegBtnOnStyle,
  mutedStyle,
  errorStyle,
  detailsSummaryStyle,
  sumttlStyle,
  sumvalStyle,
  basicsRowStyle,
  basicsLabelStyle,
  sizeOpenerStyle,
  sizeOpenerChevronStyle,
  sizeSheetSubStyle,
} from './settingsStyles';

const stackStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-4)',
};

const DEFAULT_CADENCE = 14; // shown in the stepper when none is set yet.

export function HouseholdProductSettings({
  productId,
  basicsLeading,
  productPreferencesSlot,
}: {
  productId: string;
  /** The "How many to add" stepper, owned by the detail page (it persists
   *  `default_quantity` through the page form). Rendered first inside Basics. */
  basicsLeading?: ReactNode;
  /** The Product-preferences picker, injected so it sits between Basics and
   *  Brand preferences in the prototype order. */
  productPreferencesSlot?: ReactNode;
}): JSX.Element {
  const prefsQuery = useHouseholdProductPreferences(productId);
  const brandsQuery = useBrands();
  const storesQuery = useStores();

  const linkBrand = useLinkBrandPreference();
  const setBrandPreference = useSetBrandPreference();
  const unlinkBrand = useUnlinkBrandPreference();
  const linkRetailer = useLinkRetailerPreference();
  const unlinkRetailer = useUnlinkRetailerPreference();
  const linkStore = useLinkStoreLocationPreference();
  const unlinkStore = useUnlinkStoreLocationPreference();
  const setCadence = useSetHouseholdProductCadence();

  // T36.3 — measurement (Size) section.
  const productQuery = useHouseholdProduct(productId);
  const choicesQuery = useMeasurementChoices(productId);
  const setMeasurement = useSetMeasurement();
  const setDisplayUnit = useSetDisplayUnit();
  const useSuggested = useUseSuggestedMeasurement();

  // T27.2.7.10 — product-relevant brands for the add-brand picker. The brand add
  // picker must offer brands RELEVANT to this product (Soy sauce → soy-sauce
  // brands), not the whole brand list. Derive them from the SystemProduct search
  // on the product's own name: each matching system product carries its
  // `(brand_id, brand_name)`, so the distinct branded set of those matches is the
  // brands relevant to this product. Frontend-only — reuses the existing search
  // endpoint; no new query/column. `useBrands()` is still used to LABEL existing
  // pinned chips (a pinned brand may not be in the current relevant set).
  const productName = productQuery.data?.display_name ?? '';
  const relevantBrandSearchParams: SystemProductSearchParams = useMemo(
    () => ({ query: productName.trim(), limit: 50, offset: 0 }),
    [productName]
  );
  const relevantBrandsQuery = useSearchSystemProducts(
    relevantBrandSearchParams,
    productName.trim().length > 0
  );

  const [actionError, setActionError] = useState<string | null>(null);
  // T36.3 — the Size editor moved into a bottom-sheet (Modal variant="sheet").
  // Basics shows a compact opener; tapping it opens the sheet.
  const [sizeSheetOpen, setSizeSheetOpen] = useState(false);

  const prefs = prefsQuery.data;
  const brands = brandsQuery.data?.brands ?? [];
  const stores = storesQuery.data?.stores ?? [];

  const brandName = useMemo(() => {
    const map = new Map(brands.map((b) => [b.id, b.name]));
    return (id: string) => map.get(id) ?? id;
  }, [brands]);

  const storeName = useMemo(() => {
    const map = new Map(stores.map((s) => [s.id, s.name]));
    return (id: string) => map.get(id) ?? id;
  }, [stores]);

  // Retailer (chain) catalog: derive distinct retailer_ids from the household's
  // store locations. No standalone retailer-list endpoint exists yet (a proper
  // retailer catalog is T26); for minimal-functional the household's own stores
  // are the available retailer set. The first store carrying each retailer_id
  // lends its name as the label.
  const retailerOptions = useMemo(() => {
    const byRetailer = new Map<string, string>();
    for (const s of stores) {
      if (s.retailer_id && !byRetailer.has(s.retailer_id)) {
        byRetailer.set(s.retailer_id, s.name);
      }
    }
    return Array.from(byRetailer, ([id, label]) => ({ id, label }));
  }, [stores]);

  const retailerLabel = useMemo(() => {
    const map = new Map(retailerOptions.map((r) => [r.id, r.label]));
    return (id: string) => map.get(id) ?? id;
  }, [retailerOptions]);

  const run = async (fn: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Could not update the preference'));
    }
  };

  if (prefsQuery.isLoading) {
    return (
      <div data-testid="hp-settings-loading">
        <Spinner />
      </div>
    );
  }

  if (prefsQuery.isError || !prefs) {
    return (
      <div style={setgroupStyle} data-testid="hp-settings-load-error">
        <p style={errorStyle}>Could not load preferences.</p>
      </div>
    );
  }

  const preferredBrands = prefs.brands.filter((b) => b.preference === 'preferred');
  const substituteBrands = prefs.brands.filter((b) => b.preference === 'substitute');
  const excludedBrands = prefs.brands.filter((b) => b.preference === 'exclude');
  const linkedBrandIds = new Set(prefs.brands.map((b) => b.brand_id));
  const linkedRetailerIds = new Set(prefs.retailers.map((r) => r.retailer_id));
  const linkedStoreIds = new Set(prefs.storeLocations.map((s) => s.storelocation_id));

  // T27.2.7.10 — the add-brand options are the PRODUCT-RELEVANT brands (distinct
  // branded matches from the SystemProduct search on the product name), minus the
  // ones already linked. Falls back gracefully to nothing relevant if the search
  // hasn't resolved or the product has no branded matches (the add picker then
  // shows its empty text). Only the OPTIONS source changes — the brand section's
  // look is untouched.
  const relevantBrandResults = relevantBrandsQuery.data?.items ?? [];
  const relevantBrandMap = new Map<string, string>();
  for (const r of relevantBrandResults) {
    if (r.brand_id && r.brand_name && !relevantBrandMap.has(r.brand_id)) {
      relevantBrandMap.set(r.brand_id, r.brand_name);
    }
  }
  const availableBrandOptions: AddOption[] = Array.from(relevantBrandMap, ([id, label]) => ({
    id,
    label,
  }))
    .filter((o) => !linkedBrandIds.has(o.id))
    .sort((a, b) => a.label.localeCompare(b.label));
  const availableRetailerOptions: AddOption[] = retailerOptions
    .filter((r) => !linkedRetailerIds.has(r.id))
    .map((r) => ({ id: r.id, label: r.label }));
  const availableStoreOptions: AddOption[] = stores
    .filter((s) => !linkedStoreIds.has(s.id))
    .map((s) => ({ id: s.id, label: s.name }));

  const cadenceValue = prefs.cadencedays ?? DEFAULT_CADENCE;

  const stepCadence = (delta: number) => {
    const next = Math.max(1, cadenceValue + delta);
    void run(() => setCadence.mutateAsync({ productId, cadenceDays: next }));
  };
  const clearCadence = () => {
    void run(() => setCadence.mutateAsync({ productId, cadenceDays: null }));
  };

  // Summary value lines for the collapsed Brand / Store foldables (prototype
  // `.sumval`). They surface the gist while the section stays folded.
  const preferredBrandName = preferredBrands[0] ? brandName(preferredBrands[0].brand_id) : null;
  const subBrandCount = substituteBrands.length;
  const brandSummary = preferredBrandName
    ? `★ ${preferredBrandName}${subBrandCount > 0 ? ` · ${subBrandCount} subs` : ''}`
    : subBrandCount > 0
      ? `${subBrandCount} subs`
      : 'None yet';
  const retailerNames = prefs.retailers.map((r) => retailerLabel(r.retailer_id));
  const storeCount = prefs.storeLocations.length;
  const storeWord = storeCount === 1 ? 'store' : 'stores';
  const storeSummary =
    retailerNames.length > 0 || storeCount > 0
      ? `${retailerNames.join(', ') || 'Any retailer'} · ${storeCount} ${storeWord}`
      : 'Any store';

  // The Size sheet content (T36.3 — moved out of Basics into a bottom-sheet).
  // Matches the prototype Size sheet (ADR-015, rev. 2026-06-22): a "Show in"
  // display-unit switch (now rounded PILLS — volume → mL/L/fl oz/gal, weight →
  // g/kg/lb/oz; count has no forms → no switch) over the snapshot's stored
  // forms, then the "Pick your pack" list, then "Use suggested". Choosing a unit
  // persists offline from `forms`, no recompute; choosing a pack swaps which
  // packing is shown. Logic is UNCHANGED from the prior inline build — only the
  // host moved (Basics row → Modal sheet) and the switch restyled to pills.
  const renderSizeSheet = (): JSX.Element => {
    const product = productQuery.data;
    // The chosen display unit IS the stored snapshot unit (the switch re-points
    // it). Render everything in that unit.
    const displayUnit = product?.measurement_unit ?? null;
    const resolved = product ? formatResolvedMeasurement(product) : null;
    const isUser = product?.measurement_provenance === 'user';
    // Display-unit choices = the dimension's stored forms (empty for count/loose).
    const displayUnits = product ? availableDisplayUnits(product) : [];
    const rawChoices: MeasurementChoice[] = choicesQuery.data?.choices ?? [];
    // Dedupe by rendered pack identity — `(pack_count, value, unit, dimension)`.
    // Two linked SystemProducts can each carry an identical packing (e.g. a milk
    // product linked to two SPs both with a `4 L` pack); those collapse to ONE
    // row. Within a group, keep the packing that matches the product's current
    // `source_packing_id` (so the current pick stays selectable), else the first
    // (the list arrives most-common-first). Map preserves first-seen order.
    const sourcePackingId = product?.source_packing_id ?? null;
    const dedupedChoices: MeasurementChoice[] = (() => {
      const byIdentity = new Map<string, MeasurementChoice>();
      for (const c of rawChoices) {
        const key = `${c.pack_count}|${c.value ?? ''}|${c.unit ?? ''}|${c.dimension}`;
        const existing = byIdentity.get(key);
        if (!existing) {
          byIdentity.set(key, c);
        } else if (sourcePackingId != null && c.packing_id === sourcePackingId) {
          // Prefer the packing the product currently points at, so its row
          // remains the selectable one after collapse.
          byIdentity.set(key, c);
        }
      }
      return Array.from(byIdentity.values());
    })();
    // The current pack — the deduped row whose packing the product points at.
    // Falls back to pack_count match when no packing id is stored, so a system
    // default still shows its ✓.
    const currentPackingId =
      dedupedChoices.find((c) => c.packing_id === sourcePackingId)?.packing_id ??
      dedupedChoices.find(
        (c) => c.pack_count === (product?.measurement_pack_count ?? 1),
      )?.packing_id ??
      null;

    return (
      <>
        <p style={sizeSheetSubStyle} data-testid="hp-settings-measurement-sub">
          Pick the pack you usually buy, and the units you&apos;d like to see it in.
        </p>
        <div style={setlabelStyle} data-testid="hp-settings-measurement-current">
          {resolved
            ? `Currently: ${resolved}${isUser ? ' (your pick)' : ''}`
            : 'No size yet — it appears once an offer covers this product.'}
        </div>
        {/* "Show in" — the display-unit switch. Only for weight/volume (count
            and loose products have no forms, so no switch row renders). */}
        {displayUnits.length > 0 && (
          <div style={sizeUnitRowStyle} data-testid="hp-settings-measurement-show-in">
            <span style={sizeUnitLabelStyle}>Show in</span>
            <div style={unitSegStyle}>
              {displayUnits.map((u) => {
                const on = u === displayUnit;
                return (
                  <button
                    key={u}
                    type="button"
                    aria-pressed={on}
                    style={on ? unitSegBtnOnStyle : unitSegBtnStyle}
                    onClick={() =>
                      on ? undefined : void run(() => setDisplayUnit.mutateAsync({ productId, unit: u }))
                    }
                    data-testid={`hp-settings-measurement-unit-${u.replace(/\s+/g, '-')}`}
                  >
                    {u}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {dedupedChoices.length === 0 ? (
          <span style={mutedStyle} data-testid="hp-settings-measurement-empty">
            No size options yet.
          </span>
        ) : (
          <>
            <div style={pickListSubheadStyle}>Pick your pack</div>
            <div style={pickListStyle} data-testid="hp-settings-measurement-choices">
              {dedupedChoices.map((c) => {
                // Render each pack in the chosen display unit (the formatter falls
                // back to the pack's own unit when its forms lack that unit — e.g.
                // a count pack while a volume unit is selected).
                const label = formatResolvedMeasurement(
                  {
                    measurement_pack_count: c.pack_count,
                    measurement_value: c.value,
                    measurement_unit: c.unit,
                    measurement_dimension: c.dimension,
                    measurement_forms: c.forms,
                  },
                  displayUnit,
                );
                // ✓ on the current pack regardless of provenance (system default
                // OR user pick) — matches the prototype's `.pickmark`. The "(your
                // pick)" note on the Currently line above already conveys a user
                // override; the ✓ is not gated on `isUser`.
                const selected = c.packing_id === currentPackingId;
                return (
                  <div
                    key={c.packing_id}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selected}
                    style={selected ? pickRowOnStyle : pickRowStyle}
                    onClick={() =>
                      void run(() =>
                        setMeasurement.mutateAsync({ productId, packingId: c.packing_id }),
                      )
                    }
                    data-testid={`hp-settings-measurement-choice-${c.packing_id}`}
                  >
                    <span style={pickMarkStyle} aria-hidden="true">
                      {selected ? '✓' : ''}
                    </span>
                    <span>{label ?? `${c.unit ?? ''}`}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {isUser && (
          <button
            type="button"
            style={learnLinkStyle}
            onClick={() => void run(() => useSuggested.mutateAsync({ productId }))}
            data-testid="hp-settings-measurement-use-suggested"
          >
            Use suggested
          </button>
        )}
      </>
    );
  };

  // The compact Size control rendered IN Basics (T36.3). Shows the current
  // resolved measurement + a chevron (e.g. `4 L ⌄`) that opens the sheet. With
  // no resolved measurement there is no pack to edit, so it shows the empty
  // message in place of an opener (no sheet to open).
  const renderSizeOpener = (): JSX.Element => {
    const product = productQuery.data;
    const resolved = product ? formatResolvedMeasurement(product) : null;
    if (!resolved) {
      return (
        <span style={mutedStyle} data-testid="hp-settings-measurement-current">
          No size yet — it appears once an offer covers this product.
        </span>
      );
    }
    return (
      <button
        type="button"
        style={sizeOpenerStyle}
        onClick={() => setSizeSheetOpen(true)}
        data-testid="hp-settings-measurement-open"
      >
        <span data-testid="hp-settings-measurement-opener-value">{resolved}</span>
        <span style={sizeOpenerChevronStyle} aria-hidden="true">
          ⌄
        </span>
      </button>
    );
  };

  return (
    <div style={stackStyle} data-testid="hp-settings" className="hp-settings-stack">
      {actionError && (
        <div role="alert" style={errorStyle} data-testid="hp-settings-error">
          {actionError}
        </div>
      )}

      {/* --- 1 · Basics — always-open: How many · Size · How often ---------- */}
      <section style={setgroupStyle} data-testid="hp-settings-basics" data-section="basics">
        <h4 style={h4Style}>Basics</h4>

        {/* How many to add — owned + persisted by the detail page (form). */}
        {basicsLeading}

        {/* Size (T36 measurement picker) — compact opener; the editor itself
            lives in a bottom-sheet (T36.3). */}
        <div style={basicsRowStyle} data-testid="hp-settings-measurement" data-section="size">
          <div style={basicsLabelStyle}>Size</div>
          <p style={hintStyle}>
            We use the most common size sold for this product. Pick a different one if
            you prefer.
          </p>
          {renderSizeOpener()}
        </div>

        {/* How often (cadence stepper). */}
        <div style={basicsRowStyle} data-testid="hp-settings-cadence" data-section="howoften">
          <div style={basicsLabelStyle}>How often</div>
          <p style={hintStyle}>Leave it and we&apos;ll learn the rhythm from your purchases.</p>
          <div style={cadrowStyle}>
            <span style={cadWordStyle}>Every</span>
            <div style={stepperStyle}>
              <button
                type="button"
                style={stepperBtnStyle}
                aria-label="Fewer days"
                onClick={() => stepCadence(-1)}
                data-testid="hp-settings-cadence-decrement"
              >
                −
              </button>
              <span style={stepperValueStyle} data-testid="hp-settings-cadence-value">
                {cadenceValue}
              </span>
              <button
                type="button"
                style={stepperBtnStyle}
                aria-label="More days"
                onClick={() => stepCadence(1)}
                data-testid="hp-settings-cadence-increment"
              >
                +
              </button>
            </div>
            <span style={cadWordStyle}>days</span>
            <button
              type="button"
              style={learnLinkStyle}
              onClick={clearCadence}
              data-testid="hp-settings-cadence-clear"
            >
              Let us learn it
            </button>
          </div>
          <span style={{ ...mutedStyle, marginTop: 'var(--space-2)' }} data-testid="hp-settings-cadence-status">
            {prefs.cadencedays != null
              ? `Pinned to every ${prefs.cadencedays} days.`
              : "Not set — we'll learn it from your purchases."}
          </span>
        </div>
      </section>

      {/* --- 2 · Product preferences (the foldable picker, injected) -------- */}
      {productPreferencesSlot}

      {/* --- 3 · Brand preferences — foldable, collapsed -------------------- */}
      <details style={setgroupStyle} data-testid="hp-settings-brands" data-section="brands">
        <summary style={detailsSummaryStyle}>
          <span style={sumttlStyle}>Brand preferences</span>
          <span style={sumvalStyle} data-testid="hp-settings-brands-summary">
            {brandSummary}
          </span>
        </summary>
        <p style={hintStyle}>
          Broader than a specific product — steers suggestions when you haven&apos;t
          pinned an exact one.
        </p>

        <div style={subheadStyle}>Preferred</div>
        <div style={tagsRowStyle}>
          {preferredBrands.map((b) => (
            <span style={tagPrefStyle} key={b.brand_id} data-testid={`hp-settings-brand-${b.brand_id}`}>
              <span
                role="button"
                tabIndex={0}
                aria-label={`Make ${brandName(b.brand_id)} a substitute`}
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  void run(() =>
                    setBrandPreference.mutateAsync({
                      productId,
                      brandId: b.brand_id,
                      preference: 'substitute',
                    })
                  )
                }
                data-testid={`hp-settings-brand-${b.brand_id}-toggle-preference`}
              >
                ★ {brandName(b.brand_id)}
              </span>
              <span style={{ display: 'none' }} data-testid={`hp-settings-brand-${b.brand_id}-preference`}>
                preferred
              </span>
              <span
                role="button"
                tabIndex={0}
                aria-label="Remove"
                style={tagXStyle}
                onClick={() => void run(() => unlinkBrand.mutateAsync({ productId, brandId: b.brand_id }))}
                data-testid={`hp-settings-brand-${b.brand_id}-remove`}
              >
                ✕
              </span>
            </span>
          ))}
          <InlineAddSearch
            label="+ Add brand"
            options={availableBrandOptions}
            onPick={(brandId) =>
              void run(() => linkBrand.mutateAsync({ productId, brandId, preference: 'preferred' }))
            }
            testId="hp-settings-brand"
            emptyText="No more brands to add."
          />
        </div>

        <div style={subheadStyle}>Substitutes</div>
        <div style={tagsRowStyle}>
          {substituteBrands.map((b) => (
            <span style={tagSubStyle} key={b.brand_id} data-testid={`hp-settings-brand-${b.brand_id}`}>
              <span
                role="button"
                tabIndex={0}
                aria-label={`Make ${brandName(b.brand_id)} preferred`}
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  void run(() =>
                    setBrandPreference.mutateAsync({
                      productId,
                      brandId: b.brand_id,
                      preference: 'preferred',
                    })
                  )
                }
                data-testid={`hp-settings-brand-${b.brand_id}-toggle-preference`}
              >
                {brandName(b.brand_id)}
              </span>
              <span style={{ display: 'none' }} data-testid={`hp-settings-brand-${b.brand_id}-preference`}>
                substitute
              </span>
              <span
                role="button"
                tabIndex={0}
                aria-label="Remove"
                style={tagXStyle}
                onClick={() => void run(() => unlinkBrand.mutateAsync({ productId, brandId: b.brand_id }))}
                data-testid={`hp-settings-brand-${b.brand_id}-remove`}
              >
                ✕
              </span>
            </span>
          ))}
          <InlineAddSearch
            label="+ Add"
            options={availableBrandOptions}
            onPick={(brandId) =>
              void run(() => linkBrand.mutateAsync({ productId, brandId, preference: 'substitute' }))
            }
            testId="hp-settings-brand-substitute"
            emptyText="No more brands to add."
          />
        </div>

        {/* Don't suggest — the one firm brand control. A soft downweight (T12.5
            weighs it), not a hard filter: kept out of suggestions but still
            findable by search. */}
        <div style={subheadStyle} data-testid="hp-settings-brands-exclude">
          Don&apos;t suggest
        </div>
        <p style={{ ...hintStyle, margin: `0 0 var(--space-2)` }}>
          The one firm control here — we keep these out of your suggestions. You
          can still find them by searching.
        </p>
        <div style={tagsRowStyle}>
          {excludedBrands.length === 0 && (
            <span style={mutedStyle} data-testid="hp-settings-brands-exclude-empty">
              No brands held back.
            </span>
          )}
          {excludedBrands.map((b) => (
            <span
              style={tagExclStyle}
              key={b.brand_id}
              data-testid={`hp-settings-brand-exclude-${b.brand_id}`}
            >
              🚫 {brandName(b.brand_id)}
              <span
                role="button"
                tabIndex={0}
                aria-label="Remove"
                style={tagXStyle}
                onClick={() => void run(() => unlinkBrand.mutateAsync({ productId, brandId: b.brand_id }))}
                data-testid={`hp-settings-brand-exclude-${b.brand_id}-remove`}
              >
                ✕
              </span>
            </span>
          ))}
          <InlineAddSearch
            label="+ Add"
            options={availableBrandOptions}
            onPick={(brandId) =>
              void run(() => linkBrand.mutateAsync({ productId, brandId, preference: 'exclude' }))
            }
            testId="hp-settings-brand-exclude"
            emptyText="No more brands to add."
          />
        </div>
      </details>

      {/* --- 4 · Store preferences (where to buy) — foldable, collapsed ----- */}
      <details style={setgroupStyle} data-testid="hp-settings-retailers" data-section="where">
        <summary style={detailsSummaryStyle}>
          <span style={sumttlStyle}>Store preferences</span>
          <span style={sumvalStyle} data-testid="hp-settings-stores-summary">
            {storeSummary}
          </span>
        </summary>
        <p style={hintStyle}>Preferred retailers and specific stores for this item.</p>

        <div style={setlabelStyle}>Retailers</div>
        <div style={tagsRowStyle}>
          {prefs.retailers.length === 0 && (
            <span style={mutedStyle} data-testid="hp-settings-retailers-empty">
              No preferred retailers yet.
            </span>
          )}
          {prefs.retailers.map((r) => (
            <span style={tagSubStyle} key={r.retailer_id} data-testid={`hp-settings-retailer-${r.retailer_id}`}>
              {retailerLabel(r.retailer_id)}
              <span
                role="button"
                tabIndex={0}
                aria-label="Remove"
                style={tagXStyle}
                onClick={() =>
                  void run(() => unlinkRetailer.mutateAsync({ productId, retailerId: r.retailer_id }))
                }
                data-testid={`hp-settings-retailer-${r.retailer_id}-remove`}
              >
                ✕
              </span>
            </span>
          ))}
          <InlineAddSearch
            label="+ Add retailer"
            options={availableRetailerOptions}
            onPick={(retailerId) =>
              void run(() => linkRetailer.mutateAsync({ productId, retailerId }))
            }
            testId="hp-settings-retailer"
            emptyText="No more retailers to add."
          />
        </div>

        <div style={setlabelStyle}>Stores</div>
        <div style={tagsRowStyle} data-testid="hp-settings-stores">
          {prefs.storeLocations.length === 0 && (
            <span style={mutedStyle} data-testid="hp-settings-stores-empty">
              No preferred store locations yet.
            </span>
          )}
          {prefs.storeLocations.map((s) => (
            <span style={tagSubStyle} key={s.storelocation_id} data-testid={`hp-settings-store-${s.storelocation_id}`}>
              {storeName(s.storelocation_id)}
              <span
                role="button"
                tabIndex={0}
                aria-label="Remove"
                style={tagXStyle}
                onClick={() =>
                  void run(() =>
                    unlinkStore.mutateAsync({ productId, storeLocationId: s.storelocation_id })
                  )
                }
                data-testid={`hp-settings-store-${s.storelocation_id}-remove`}
              >
                ✕
              </span>
            </span>
          ))}
          <InlineAddSearch
            label="+ Add store"
            options={availableStoreOptions}
            onPick={(storeLocationId) =>
              void run(() => linkStore.mutateAsync({ productId, storeLocationId }))
            }
            testId="hp-settings-store"
            emptyText="No more stores to add."
          />
        </div>
      </details>

      {/* Size editor — bottom-sheet (T36.3). Opened from the Basics Size
          opener; ×, overlay-click, and Escape close it (handled by Modal). */}
      <Modal
        open={sizeSheetOpen}
        onClose={() => setSizeSheetOpen(false)}
        title="Size"
        variant="sheet"
      >
        <div data-testid="hp-settings-measurement-sheet">{renderSizeSheet()}</div>
      </Modal>
    </div>
  );
}
